// src/pages/Profile/Profile.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Profile.css';

interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
  provider: string;
}

// SVG Icons
const StatsIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 16v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2M7 10l3-3 3 3 4-4" />
    <path d="M17 10V4h-3" />
  </svg>
);

const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="20 6 9 17 4 12" />
  </svg>
);

const TargetIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const GoogleBadgeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <path
      fill="currentColor"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="currentColor"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
  </svg>
);

const DemoBadgeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const Profile: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        setUser(JSON.parse(userStr));
      } catch (e) {
        console.error('Failed to parse user');
        navigate('/login');
      }
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const getInitials = (name: string): string => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getProviderBadge = (provider: string) => {
    switch (provider) {
      case 'google':
        return { text: 'Google', icon: <GoogleBadgeIcon />, class: 'google' };
      case 'demo':
        return { text: 'Demo Account', icon: <DemoBadgeIcon />, class: 'demo' };
      default:
        return { text: 'Unknown', icon: null, class: '' };
    }
  };

  if (!user) {
    return (
      <div className="profile-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  const provider = getProviderBadge(user.provider);

  return (
    <div className="profile-container">
      <div className="profile-paper">
        <h1 className="profile-title">Profile</h1>
        
        <div className="profile-content">
          {/* Avatar Section */}
          <div className="profile-avatar-section">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="profile-avatar" />
            ) : (
              <div className="profile-avatar-placeholder">
                {getInitials(user.name)}
              </div>
            )}
            
            <div className={`provider-badge ${provider.class}`}>
              <span className="provider-icon">{provider.icon}</span>
              <span className="provider-text">{provider.text}</span>
            </div>
          </div>

          {/* Info Section */}
          <div className="profile-info-section">
            <div className="info-group">
              <label className="info-label">Name</label>
              <div className="info-value">{user.name}</div>
            </div>

            <div className="info-group">
              <label className="info-label">Email</label>
              <div className="info-value">{user.email}</div>
            </div>

            <div className="info-group">
              <label className="info-label">User ID</label>
              <div className="info-value mono">{user.id}</div>
            </div>

            <div className="info-group">
              <label className="info-label">Account Type</label>
              <div className="info-value">
                {user.provider === 'google' ? 'Google Account' : 'Demo Account'}
              </div>
            </div>
          </div>

          {/* Stats Section */}
          <div className="profile-stats-section">
            <h2 className="stats-title">Account Statistics</h2>
            
            <div className="stats-grid">
              <div className="stat-card">
                <span className="stat-icon"><StatsIcon /></span>
                <span className="stat-label">Predictions Made</span>
                <span className="stat-value">0</span>
              </div>
              
              <div className="stat-card">
                <span className="stat-icon"><CheckIcon /></span>
                <span className="stat-label">Correct</span>
                <span className="stat-value">0</span>
              </div>
              
              <div className="stat-card">
                <span className="stat-icon"><TargetIcon /></span>
                <span className="stat-label">Accuracy</span>
                <span className="stat-value">0%</span>
              </div>
              
              <div className="stat-card">
                <span className="stat-icon"><CalendarIcon /></span>
                <span className="stat-label">Member Since</span>
                <span className="stat-value">Today</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;